import { Button } from '@ksyos/design-system';
import { every, isNull, values } from 'lodash';
import type { ValidationErrors } from '../../../../../utils/validation';
import { risicofactorenEcgCustomControlDataSchema } from '../../../../../../common/validation/customControls/cardiology/holterConsult/cardiology';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../../../utils/validation';
import {
    FormInlineGroup,
    FormParagraph,
    InputTriState,
    NullableInputText,
    ThirdHeader,
} from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { Modal } from '../../../../elements/modals/Modal';
import { AnamneseType } from './AnamneseType';
import { AnamneseWrapper } from './AnamneseWrapper';

interface IRisicofactorenAddEditModalProps {
    anamneseData: IRisicofactoren | null;
    deleteAnamneseData: () => Promise<void>;
    onClose: () => Promise<void>;
    setAnamneseData: (item?: IRisicofactoren) => Promise<void>;
}

interface IRisicofactorenAddEditModalState {
    anamneseData: IRisicofactoren;
    errors: ValidationErrors;
}

export interface IRisicofactoren {
    adipositas: boolean | null;
    cardiomyopathie: boolean | null;
    diabetes_mellitus: boolean | null;
    hypercholesterolemie: boolean | null;
    hypertensie: boolean | null;
    klepafwijkingen: boolean | null;
    myocardinfarct: boolean | null;
    onbekend: boolean | null;
    overig: string | null;
    pacemaker_implantatie: boolean | null;
    PCI_of_CABG_60e_jaar_bij_1e_graads_familieleden: boolean | null;
    plotselinge_hartdood: boolean | null;
    positieve_familieanamnese_voor_hart_en_vaatziekten: boolean | null;
    reumatische_aandoening: boolean | null;
    roken: boolean | null;
}

export class RisicofactorenAddEditModal extends Component<
    IRisicofactorenAddEditModalProps,
    IRisicofactorenAddEditModalState
> {
    constructor(props: IRisicofactorenAddEditModalProps) {
        super(props);
        this.state = {
            anamneseData: {
                hypertensie: null,
                roken: null,
                hypercholesterolemie: null,
                positieve_familieanamnese_voor_hart_en_vaatziekten: null,
                diabetes_mellitus: null,
                adipositas: null,
                reumatische_aandoening: null,
                myocardinfarct: null,
                PCI_of_CABG_60e_jaar_bij_1e_graads_familieleden: null,
                cardiomyopathie: null,
                pacemaker_implantatie: null,
                plotselinge_hartdood: null,
                klepafwijkingen: null,
                onbekend: null,
                overig: null,

                ...this.props.anamneseData,
            },
            errors: emptyValidationErrors,
        };
    }

    public render() {
        return (
            <Modal
                actionButtons={[
                    <Button
                        onClick={async () => await this.props.deleteAnamneseData()}
                        variant="outline"
                    >
                        Verwijder
                    </Button>,
                    <Button
                        onClick={async () => await this.validateAnamneseData()}
                        variant="solid"
                    >
                        Anamnese opslaan
                    </Button>,
                ]}
                isOpen
                onClose={async () => await this.props.onClose()}
                size="medium"
                title="Anamnese risicofactoren"
            >
                <AnamneseWrapper anamneseType={AnamneseType.risicofactoren}>
                    <ThirdHeader text="Algemene risicofactoren" />

                    <HorizontalFormGroup hasErrors={this.state.errors.has('hypertensie')}>
                        <FormLabel>Hypertensie</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('hypertensie')}
                                name="hypertensie"
                                onChange={async (value) => await this.handleChange('hypertensie', value)}
                                onDoValidate={async () => await this.validateOne('hypertensie')}
                                value={this.state.anamneseData.hypertensie}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('roken')}>
                        <FormLabel>Roken</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('roken')}
                                name="roken"
                                onChange={async (value) => await this.handleChange('roken', value)}
                                onDoValidate={async () => await this.validateOne('roken')}
                                value={this.state.anamneseData.roken}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('hypercholesterolemie')}>
                        <FormLabel>Hypercholesterolemie</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('hypercholesterolemie')}
                                name="hypercholesterolemie"
                                onChange={async (value) => await this.handleChange('hypercholesterolemie', value)}
                                onDoValidate={async () => await this.validateOne('hypercholesterolemie')}
                                value={this.state.anamneseData.hypercholesterolemie}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup
                        hasErrors={this.state.errors.has('positieve_familieanamnese_voor_hart_en_vaatziekten')}
                    >
                        <FormLabel>Positieve familieanamnese voor hart- en vaatziekten</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('positieve_familieanamnese_voor_hart_en_vaatziekten')}
                                name="positieve_familieanamnese_voor_hart_en_vaatziekten"
                                onChange={async (value) =>
                                    await this.handleChange('positieve_familieanamnese_voor_hart_en_vaatziekten', value)
                                }
                                onDoValidate={async () =>
                                    await this.validateOne('positieve_familieanamnese_voor_hart_en_vaatziekten')
                                }
                                value={this.state.anamneseData.positieve_familieanamnese_voor_hart_en_vaatziekten}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('diabetes_mellitus')}>
                        <FormLabel>Diabetes mellitus</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('diabetes_mellitus')}
                                name="diabetes_mellitus"
                                onChange={async (value) => await this.handleChange('diabetes_mellitus', value)}
                                onDoValidate={async () => await this.validateOne('diabetes_mellitus')}
                                value={this.state.anamneseData.diabetes_mellitus}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('adipositas')}>
                        <FormLabel>Adipositas</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('adipositas')}
                                name="adipositas"
                                onChange={async (value) => await this.handleChange('adipositas', value)}
                                onDoValidate={async () => await this.validateOne('adipositas')}
                                value={this.state.anamneseData.adipositas}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('reumatische_aandoening')}>
                        <FormLabel>Reumatische aandoening</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('reumatische_aandoening')}
                                name="reumatische_aandoening"
                                onChange={async (value) => await this.handleChange('reumatische_aandoening', value)}
                                onDoValidate={async () => await this.validateOne('reumatische_aandoening')}
                                value={this.state.anamneseData.reumatische_aandoening}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <ThirdHeader text="Familiaire hart- en vaatziekten" />

                    <HorizontalFormGroup hasErrors={this.state.errors.has('myocardinfarct')}>
                        <FormLabel>Myocardinfarct</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('myocardinfarct')}
                                name="myocardinfarct"
                                onChange={async (value) => await this.handleChange('myocardinfarct', value)}
                                onDoValidate={async () => await this.validateOne('myocardinfarct')}
                                value={this.state.anamneseData.myocardinfarct}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup
                        hasErrors={this.state.errors.has('PCI_of_CABG_60e_jaar_bij_1e_graads_familieleden')}
                    >
                        <FormLabel>PCI of CABG &lt; 60e jaar bij 1e graads familieleden</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('PCI_of_CABG_60e_jaar_bij_1e_graads_familieleden')}
                                name="PCI_of_CABG_60e_jaar_bij_1e_graads_familieleden"
                                onChange={async (value) =>
                                    await this.handleChange('PCI_of_CABG_60e_jaar_bij_1e_graads_familieleden', value)
                                }
                                onDoValidate={async () =>
                                    await this.validateOne('PCI_of_CABG_60e_jaar_bij_1e_graads_familieleden')
                                }
                                value={this.state.anamneseData.PCI_of_CABG_60e_jaar_bij_1e_graads_familieleden}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('cardiomyopathie')}>
                        <FormLabel>Cardiomyopathie</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('cardiomyopathie')}
                                name="cardiomyopathie"
                                onChange={async (value) => await this.handleChange('cardiomyopathie', value)}
                                onDoValidate={async () => await this.validateOne('cardiomyopathie')}
                                value={this.state.anamneseData.cardiomyopathie}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('pacemaker_implantatie')}>
                        <FormLabel>Pacemaker implantatie</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('pacemaker_implantatie')}
                                name="pacemaker_implantatie"
                                onChange={async (value) => await this.handleChange('pacemaker_implantatie', value)}
                                onDoValidate={async () => await this.validateOne('pacemaker_implantatie')}
                                value={this.state.anamneseData.pacemaker_implantatie}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('plotselinge_hartdood')}>
                        <FormLabel>Plotselinge hartdood</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('plotselinge_hartdood')}
                                name="plotselinge_hartdood"
                                onChange={async (value) => await this.handleChange('plotselinge_hartdood', value)}
                                onDoValidate={async () => await this.validateOne('plotselinge_hartdood')}
                                value={this.state.anamneseData.plotselinge_hartdood}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('klepafwijkingen')}>
                        <FormLabel>Klepafwijkingen</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('klepafwijkingen')}
                                name="klepafwijkingen"
                                onChange={async (value) => await this.handleChange('klepafwijkingen', value)}
                                onDoValidate={async () => await this.validateOne('klepafwijkingen')}
                                value={this.state.anamneseData.klepafwijkingen}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('onbekend')}>
                        <FormLabel>Onbekend</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('onbekend')}
                                name="onbekend"
                                onChange={async (value) => await this.handleChange('onbekend', value)}
                                onDoValidate={async () => await this.validateOne('onbekend')}
                                value={this.state.anamneseData.onbekend}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <ThirdHeader text="Toelichting risicofactoren" />

                    <HorizontalFormGroup hasErrors={this.state.errors.has('overig')}>
                        <FormLabel>Toelichting</FormLabel>
                        <InputContainer>
                            <NullableInputText
                                error={this.state.errors.get('overig')}
                                onChange={async (value) => await this.handleChange('overig', value)}
                                onDoValidate={async () => await this.validateOne('overig')}
                                value={this.state.anamneseData.overig}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>
                </AnamneseWrapper>
            </Modal>
        );
    }

    private async handleChange<K extends keyof IRisicofactoren>(name: K, value: IRisicofactoren[K]) {
        await this.setState(({ anamneseData }) => ({ anamneseData: { ...anamneseData, [name]: value } }));
    }

    private async validateOne(name: keyof IRisicofactoren) {
        await this.setState(({ anamneseData, errors }) => ({
            errors: updateValidationErrorsForOne(risicofactorenEcgCustomControlDataSchema, anamneseData, errors, name),
        }));
    }

    private async validateAnamneseData() {
        if (every(values(this.state.anamneseData), isNull)) {
            return;
        }

        const { hasErrors, errors, item } = validate(risicofactorenEcgCustomControlDataSchema, this.state.anamneseData);
        await this.setState({ errors });
        if (!hasErrors) {
            await this.props.setAnamneseData(item);
        }
    }
}

export function RisicofactorenRead(props: IRisicofactoren) {
    return (
        <>
            {showAlgemeneRisicofactoren() && (
                <HorizontalFormGroupRead>
                    <FormLabel>Algemene risicofactoren</FormLabel>
                    <InputContainer>
                        <FormParagraph>
                            {props.hypertensie !== null && (
                                <FormInlineGroup
                                    label="Hypertensie"
                                    value={props.hypertensie}
                                />
                            )}
                            {props.roken !== null && (
                                <FormInlineGroup
                                    label="Roken"
                                    value={props.roken}
                                />
                            )}
                            {props.hypercholesterolemie !== null && (
                                <FormInlineGroup
                                    label="Hypercholesterolemie"
                                    value={props.hypercholesterolemie}
                                />
                            )}
                            {props.positieve_familieanamnese_voor_hart_en_vaatziekten !== null && (
                                <FormInlineGroup
                                    label="Positieve familieanamnese voor hart- en vaatziekten"
                                    value={props.positieve_familieanamnese_voor_hart_en_vaatziekten}
                                />
                            )}
                            {props.diabetes_mellitus !== null && (
                                <FormInlineGroup
                                    label="Diabetes mellitus"
                                    value={props.diabetes_mellitus}
                                />
                            )}
                            {props.adipositas !== null && (
                                <FormInlineGroup
                                    label="Adipositas"
                                    value={props.adipositas}
                                />
                            )}
                            {props.reumatische_aandoening !== null && (
                                <FormInlineGroup
                                    label="Reumatische aandoening"
                                    value={props.reumatische_aandoening}
                                />
                            )}
                        </FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {showFamiliairHartEnVaatziekten() && (
                <HorizontalFormGroupRead>
                    <FormLabel>Familiaire hart- en vaatziekten</FormLabel>
                    <InputContainer>
                        <FormParagraph>
                            {props.myocardinfarct !== null && (
                                <FormInlineGroup
                                    label="Myocardinfarct"
                                    value={props.myocardinfarct}
                                />
                            )}
                            {props.PCI_of_CABG_60e_jaar_bij_1e_graads_familieleden !== null && (
                                <FormInlineGroup
                                    label="PCI of CABG < 60e jaar bij 1e graads familieleden"
                                    value={props.PCI_of_CABG_60e_jaar_bij_1e_graads_familieleden}
                                />
                            )}
                            {props.cardiomyopathie !== null && (
                                <FormInlineGroup
                                    label="Cardiomyopathie"
                                    value={props.cardiomyopathie}
                                />
                            )}
                            {props.pacemaker_implantatie !== null && (
                                <FormInlineGroup
                                    label="Pacemaker implantatie"
                                    value={props.pacemaker_implantatie}
                                />
                            )}
                            {props.plotselinge_hartdood !== null && (
                                <FormInlineGroup
                                    label="Plotselinge hartdood"
                                    value={props.plotselinge_hartdood}
                                />
                            )}
                            {props.klepafwijkingen !== null && (
                                <FormInlineGroup
                                    label="Klepafwijkingen"
                                    value={props.klepafwijkingen}
                                />
                            )}
                            {props.onbekend !== null && (
                                <FormInlineGroup
                                    label="Onbekend"
                                    value={props.onbekend}
                                />
                            )}
                        </FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {props.overig && (
                <HorizontalFormGroupRead>
                    <FormLabel>Toelichting risicofactoren</FormLabel>
                    <InputContainer>
                        <FormParagraph>
                            <FormInlineGroup value={props.overig} />
                        </FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}
        </>
    );

    function showAlgemeneRisicofactoren() {
        return (
            props.hypertensie !== null ||
            props.roken !== null ||
            props.hypercholesterolemie !== null ||
            props.positieve_familieanamnese_voor_hart_en_vaatziekten !== null ||
            props.diabetes_mellitus !== null ||
            props.adipositas !== null ||
            props.reumatische_aandoening !== null
        );
    }

    function showFamiliairHartEnVaatziekten() {
        return (
            props.myocardinfarct !== null ||
            props.PCI_of_CABG_60e_jaar_bij_1e_graads_familieleden !== null ||
            props.cardiomyopathie !== null ||
            props.pacemaker_implantatie !== null ||
            props.plotselinge_hartdood !== null ||
            props.klepafwijkingen !== null ||
            props.onbekend !== null
        );
    }
}
